/**
 * Follows the character's center of mass with velocity look-ahead and
 * exponential smoothing. Never cuts except on a respawn snap. Positions are
 * kept as floats and rendered at sub-pixel precision.
 */

import { halfLifeFactor, type RenderFeel } from "./feel";
import type { ViewState } from "./view";

export class Camera {
  /** World position at the center of the view, m. */
  x = 0;
  y = 0;
  private initialized = false;
  /** Pixels per meter, from the view width. */
  ppm = 80;
  screenW = 1920;
  screenH = 1080;
  /**
   * Meters the camera holds above the body, so more of what is overhead is in view and less of the
   * ground under the feet. The app sets it per viewport from `cameraRise` or `cameraRiseSplit`.
   */
  offsetY = 0;

  resize(w: number, h: number, feel: RenderFeel): void {
    this.screenW = w;
    this.screenH = h;
    this.ppm = w / feel.viewWidth;
  }

  snapTo(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.initialized = true;
  }

  update(view: ViewState, maxRunSpeed: number, feel: RenderFeel, dt: number): void {
    const comX = view.x;
    const comY = view.y + view.comY + this.offsetY;
    const targetX = comX + feel.cameraLookAhead * (view.vx / maxRunSpeed);
    if (!this.initialized || view.snapped) {
      this.snapTo(targetX, comY);
      return;
    }
    const kx = halfLifeFactor(feel.cameraHalfLifeX, dt);
    this.x += (targetX - this.x) * kx;
    const half = feel.cameraDeadBandY * 0.5;
    let desiredY = this.y;
    if (comY - this.y > half) desiredY = comY - half;
    else if (this.y - comY > half) desiredY = comY + half;
    const ky = halfLifeFactor(feel.cameraHalfLifeY, dt);
    this.y += (desiredY - this.y) * ky;
  }

  /** Screen x of a world x (y grows downward on screen). */
  toScreenX(wx: number): number {
    return this.screenW * 0.5 + (wx - this.x) * this.ppm;
  }

  toScreenY(wy: number): number {
    return this.screenH * 0.5 - (wy - this.y) * this.ppm;
  }
}
